// TopicWorkspace — Central per-topic learning hub
//
// Loads topic + topicNote from Firestore, displays concept map (read-only)
// and WorkspaceSidebar for mode selection. Triggers cold start seeding
// when conceptMap is empty.

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAuth } from 'firebase/auth';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useConceptMap } from '@hooks/useConceptMap';
import { useSessions } from '@hooks/useSessions';
import { getTopic } from '@services/firebase/topicsService';
import ConceptMap from '../visualizations/ConceptMap';
import WorkspaceSidebar from './WorkspaceSidebar';
import WorkspaceGuide from './WorkspaceGuide';
import { WorkspaceTransition } from './WorkspaceTransition';
import Layout from '../layout/Layout';
import Button from '../ui/Button';
import AnnaGlow from '../ui/AnnaGlow';
import type { TeachingForm, Topic } from '../../types';

export default function TopicWorkspace() {
  const { journeyId, topicId } = useParams<{ journeyId: string; topicId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('session');
  const { user } = useAuth();
  const { addToast } = useToast();
  const { createNewSession, sessions } = useSessions();

  // Topic data
  const [topic, setTopic] = useState<Topic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Cold start
  const [isSeeding, setIsSeeding] = useState(false);
  const [seedError, setSeedError] = useState(false);
  const seededRef = useRef(false);

  // Session creation
  const [isCreating, setIsCreating] = useState(false);

  // Workspace entry transition
  const [showTransition, setShowTransition] = useState(true);

  // Tab state — 'guide' or 'map'. First visit defaults to 'guide'.
  const storageKey = useMemo(() => `lyra_workspace_tab_${topicId}`, [topicId]);
  const [activeTab, setActiveTab] = useState<'guide' | 'map'>(() => {
    const stored = localStorage.getItem(storageKey);
    return stored === 'map' ? 'map' : 'guide';
  });

  // Persist tab choice
  useEffect(() => {
    localStorage.setItem(storageKey, activeTab);
  }, [activeTab, storageKey]);

  // Concept map hook
  const conceptMap = useConceptMap({
    userId: user?.uid || '',
    journeyId: journeyId || '',
    topicId: topicId || '',
    topicTitle: topic?.title || '',
  });

  // Load topic data
  useEffect(() => {
    if (!journeyId || !topicId) {
      setError(t('workspace.errors.loadFailed'));
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function load() {
      try {
        const result = await getTopic(journeyId!, topicId!);
        if (cancelled) return;

        if (!result.success || !result.topic) {
          setError(t('workspace.errors.loadFailed'));
          setLoading(false);
          return;
        }

        setTopic(result.topic);
        setLoading(false);

        // Persist last active workspace for quick re-entry
        localStorage.setItem('lyra_lastActiveWorkspace', JSON.stringify({
          journeyId,
          topicId,
          topicTitle: result.topic.title || '',
        }));
      } catch {
        if (!cancelled) {
          setError(t('workspace.errors.loadFailed'));
          setLoading(false);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [journeyId, topicId, t]);

  // Cold start: seed concept map when empty and topic is loaded
  useEffect(() => {
    if (
      !topic ||
      conceptMap.isLoading ||
      conceptMap.nodes.length > 0 ||
      isSeeding ||
      seedError ||
      seededRef.current
    ) return;

    const seed = async () => {
      seededRef.current = true;
      setIsSeeding(true);
      setSeedError(false);

      try {
        const token = await getAuth().currentUser?.getIdToken();
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const res = await fetch('/api/seed-concepts', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            topicTitle: topic.title,
            topicDescription: topic.description,
            subtopics: (topic.subtopics || []).map(s => s.title),
          }),
        });

        if (!res.ok) throw new Error('Seed failed');

        const data = await res.json();

        // Process seed data as a synthetic tool result.
        // Must match show_concept_map tool schema: action + nodes + edges.
        conceptMap.processToolResult({
          id: `seed_${Date.now()}`,
          toolName: 'show_concept_map',
          input: {
            action: 'add_nodes',
            nodes: data.nodes.map((n: { id: string; label: string; bloomLevel: string }) => ({
              id: n.id,
              label: n.label,
              bloomLevel: n.bloomLevel,
            })),
            edges: data.edges.map((e: { source: string; target: string; label?: string }) => ({
              source: e.source,
              target: e.target,
              label: e.label,
            })),
          },
          timestamp: new Date(),
        });
      } catch {
        setSeedError(true);
        seededRef.current = false; // Allow retry
      } finally {
        setIsSeeding(false);
      }
    };

    seed();
  }, [topic, conceptMap.isLoading, conceptMap.nodes.length, isSeeding, seedError]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stable callback for transition completion
  const handleTransitionComplete = useCallback(() => {
    setShowTransition(false);
  }, []);

  // Handle mode selection → create session.
  // Picks subtopic with lowest touchCount, tie-breaks by order.
  const handleSelectMode = useCallback(async (form: TeachingForm) => {
    if (!topic || !journeyId || !topicId || !user?.uid || isCreating) return;

    setIsCreating(true);

    try {
      const subtopics = [...(topic.subtopics || [])];
      subtopics.sort((a, b) => {
        const touchDiff = (a.touchCount || 0) - (b.touchCount || 0);
        if (touchDiff !== 0) return touchDiff;
        return (a.order || 0) - (b.order || 0);
      });
      const subtopic = subtopics[0];

      if (!subtopic) {
        addToast(t('workspace.errors.loadFailed'), 'error');
        setIsCreating(false);
        return;
      }

      // createNewSession returns { success, id?, error? } — not a bare string
      const result = await createNewSession({
        topicId,
        topicTitle: topic.title,
        teachingForm: form,
        subtopicIds: [subtopic.id],
        learningJourneyId: journeyId,
      });

      if (result.success && result.id) {
        navigate(`/session/${result.id}`);
      } else {
        addToast(t('workspace.errors.loadFailed'), 'error');
        setIsCreating(false);
      }
    } catch {
      addToast(t('workspace.errors.loadFailed'), 'error');
      setIsCreating(false);
    }
  }, [topic, journeyId, topicId, user?.uid, isCreating, createNewSession, navigate, addToast, t]);

  // Calculate total touch count across subtopics for progress display
  const totalTouchCount = (topic?.subtopics || []).reduce(
    (sum, s) => sum + (s.touchCount || 0), 0
  );

  // Completed sessions for current topic — used for exam readiness badge
  const topicSessions = topicId
    ? sessions.filter(s => s.topicId === topicId)
    : [];

  // Loading state
  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[calc(100dvh-64px)]">
          <div className="animate-pulse text-gray-400 text-sm">
            {t('connection.connecting')}
          </div>
        </div>
      </Layout>
    );
  }

  // Error state
  if (error || !topic) {
    return (
      <Layout>
        <div className="max-w-md mx-auto px-4 py-16 text-center">
          <p className="text-gray-400 mb-4">{error || t('workspace.errors.loadFailed')}</p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => window.location.reload()}>
              {t('workspace.errors.retry')}
            </Button>
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              {t('workspace.errors.backToDashboard')}
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  // Concept map loading state covers initial Firestore load AND seeding
  const mapLoading = conceptMap.isLoading || isSeeding;

  return (
    <>
      {showTransition && (
        <WorkspaceTransition onComplete={handleTransitionComplete} />
      )}
      <Layout>
        <div className="flex flex-col h-[calc(100dvh-64px)]">
        {/* Breadcrumb */}
        <nav aria-label="breadcrumb" className="px-4 py-2 text-xs text-gray-500 bg-gray-900/30 border-b border-gray-800/50 flex items-center gap-1.5">
          <button onClick={() => navigate(`/library/${journeyId}`)} className="hover:text-gray-300 transition-colors">
            {t('workspace.breadcrumb.library', 'Bibliotek')}
          </button>
          <span aria-hidden="true">›</span>
          <span className="text-gray-400 truncate max-w-[200px]">{topic.title}</span>
        </nav>

        {/* Tab bar */}
        <div role="tablist" className="flex border-b border-gray-800 bg-gray-900/50 px-4">
          <button
            id="tab-guide"
            role="tab"
            aria-selected={activeTab === 'guide'}
            aria-controls="tabpanel-workspace"
            onClick={() => setActiveTab('guide')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
              activeTab === 'guide'
                ? 'border-white text-white'
                : 'border-transparent text-gray-500 hover:text-gray-300'
            }`}
          >
            <AnnaGlow size="sm" className="scale-[0.4] -m-2" />
            {t('workspace.guide.tabGuide')}
          </button>
          <button
            id="tab-map"
            role="tab"
            aria-selected={activeTab === 'map'}
            aria-controls="tabpanel-workspace"
            onClick={() => setActiveTab('map')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'map'
                ? 'border-white text-white'
                : 'border-transparent text-gray-500 hover:text-gray-300'
            }`}
          >
            {t('workspace.guide.tabConceptMap')}
          </button>
        </div>

        {/* Tab content */}
        <div role="tabpanel" id="tabpanel-workspace" aria-labelledby={activeTab === 'guide' ? 'tab-guide' : 'tab-map'} className="flex-1 min-h-0">
          {activeTab === 'guide' ? (
            <WorkspaceGuide
              topicTitle={topic.title}
              topicDescription={topic.description}
              conceptMapNodes={conceptMap.nodes}
              isCreating={isCreating}
              onSelectMode={handleSelectMode}
              onGoToMap={() => setActiveTab('map')}
            />
          ) : (
            <div className="flex h-full">
              {/* Concept Map — 70% */}
              <div className="flex-[7] relative">
                {seedError && conceptMap.nodes.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-gray-500 gap-3">
                    <p className="text-sm">{t('workspace.coldStart.error')}</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSeedError(false)}
                    >
                      {t('workspace.coldStart.retry')}
                    </Button>
                  </div>
                ) : (
                  <ConceptMap
                    nodes={conceptMap.nodes}
                    edges={conceptMap.edges}
                    isLoading={mapLoading}
                    onNodeDragStop={conceptMap.onNodeDragStop}
                    readOnly
                  />
                )}
              </div>

              {/* Sidebar — 30% */}
              <div className="flex-[3] min-w-[280px] max-w-[400px]">
                <WorkspaceSidebar
                  topicTitle={topic.title}
                  topicDescription={topic.description}
                  conceptMapNodes={conceptMap.nodes}
                  subtopicTouchCount={totalTouchCount}
                  isCreating={isCreating}
                  journeyId={journeyId!}
                  topicId={topicId!}
                  onSelectMode={handleSelectMode}
                  examSessions={topicSessions}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
    </>
  );
}
